import { useState } from "react";
import QuestionLayout from "./QuestionLayout";
import SymbolPalette from "./staff/SymbolPalette";
import KeySignatureInputWidget from "./KeySignatureInput.jsx";
import GrandStaffSetupWidget from "./StaffSetup.jsx";
import { renderStaffLines, renderClef } from "./staff/staffRender";
import {
  snapYToPitch,
  pitchToY,
  pitchToStaffIndex
} from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs.jsx";

/* ───────────── CONSTANTS ───────────── */

const STAFF_HEIGHT = 60;
const STAFF_GAP = 50;

const TREBLE_STAFF_TOP = 40;
const BASS_STAFF_TOP = TREBLE_STAFF_TOP + STAFF_HEIGHT + STAFF_GAP;

const LEDGER_ALLOWANCE = 40;

const LEFT_MARGIN = 100;
const DEFAULT_SLOT_WIDTH = 140;

const KEY_SIG_X_START = LEFT_MARGIN;

const SYSTEM_TOP = TREBLE_STAFF_TOP;
const SYSTEM_BOTTOM = BASS_STAFF_TOP + STAFF_HEIGHT;

const BARLINE_X = LEFT_MARGIN - 12;   // just left of first notes
const BRACE_X = BARLINE_X - 16;       // brace sits left of barline
const BRACE_PATH = "M-12 -656c0 -352 -132 -696 -132 -1032c0 -180 36 -344 156 -484c4 -4 8 -8 8 -12c0 -8 -12 -16 -20 -16c-4 0 -8 0 -12 4c-160 184 -216 416 -216 656c0 360 140 712 140 1052c0 176 -40 344 -160 480v8v8c120 136 160 304 160 480c0 340 -140 692 -140 1052 c0 240 56 472 216 656c4 4 8 4 12 4c8 0 20 -8 20 -16c0 -4 -4 -8 -8 -12c-120 -140 -156 -304 -156 -484c0 -336 132 -680 132 -1032c0 -240 -52 -472 -208 -656c156 -184 208 -416 208 -656z";

const SCALE_LABEL_X = LEFT_MARGIN;
const SCALE_LABEL_Y = SYSTEM_BOTTOM + 20;// + STAFF_HEIGHT; // vertically centered on staff

function AccidentalPalette({ selected, onSelect, showInstruction = false }) {
  const buttons = [
    { label: "♭♭", value: "bb" },
    { label: "♭", value: "b" },
    { label: "♯", value: "#" },
    { label: "𝄪", value: "##" },
    { label: "none", value: "" }
  ];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginTop: 6,
        flexWrap: "nowrap"
      }}
    >
      <div style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
        {buttons.map(({ label, value }) => {
          const active = selected === value;

          return (
            <button
              key={label}
              type="button"
              onClick={() => onSelect(value)}
              style={{
                fontSize: 16,
                padding: "4px 8px",
                border: active ? "2px solid #1976d2" : "1px solid #aaa",
                background: active ? "#e3f2fd" : "#fff",
                borderRadius: 4,
                cursor: "pointer"
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {showInstruction && (
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.2,
            color: "#444",
            minWidth: 0,
            maxWidth: 430
          }}
        >
          To add an accidental, click it in the menu before you place a note.
        </div>
      )}
    </div>
  );
}
function AccidentalButtons({ onSelect }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[
        { label: "♭", value: "♭" },
        { label: "♯", value: "♯" },
      ].map(({ label, value }) => (
        <button
          key={value}
          tabIndex={-1}
          style={{
            fontSize: 16,
            padding: "2px 6px",
            cursor: "pointer"
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(value);
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
/* ───────────── LEDGER LINES ───────────── */

// function LedgerLines({ pitch, clef }) {
//   const index = pitchToStaffIndex(pitch, clef);
//   if (index >= -1 && index <= 9) return null;

//   const lines = [];
//   const lw = 6;

//   const ys =
//     index < -1
//       ? Array.from({ length: Math.ceil((-1 - index) / 2) }).map(
//           (_, i) => -(i + 1) * 10
//         )
//       : Array.from({ length: Math.ceil((index - 9) / 2) }).map(
//           (_, i) => (i + 1) * 10
//         );

//   ys.forEach((y, i) => {
//     lines.push(
//       <line
//         key={i}
//         x1={-lw}
//         x2={lw + 18}
//         y1={y}
//         y2={y}
//         stroke="#000"
//         strokeWidth={1}
//       />
//     );
//   });

//   return <g>{lines}</g>;
// }
function projectGivenNotesToGrandStaff(given) {
  const [low, high] = given.pitches;

  if (given.split) {
    return {
      treble: [{ pitch: high, symbol: given.symbol, stemUp: false }],
      bass:   [{ pitch: low,  symbol: given.symbol, stemUp: true  }]
    };
  }

  // default: same staff (treble)
  return {
    treble: [
      { pitch: low,  symbol: given.symbol, stemUp: true },
      { pitch: high, symbol: given.symbol, stemUp: false }
    ],
    bass: []
  };
}

function accidentalsToKeySignature(accidentals) {
  const slot = accidentals[1];
  if (!slot || slot.length === 0) return null;

  const sharpCount = slot.filter(a => a.accidental === "#").length;
  const flatCount  = slot.filter(a => a.accidental === "b").length;

  if (sharpCount && flatCount) return null; // invalid mixed KS

  if (sharpCount) return { type: "sharps", count: sharpCount };
  if (flatCount)  return { type: "flats", count: flatCount };

  return null;
}

function applyKeySignature(pitch, staff, keySignature) {
  if (!pitch || !keySignature) return pitch;

  const match = pitch.match(/^([A-G])([#b]?)(\d)$/);
  if (!match) return pitch;

  const [, letter, accidental, octave] = match;

  // If user already has an accidental, respect it
  if (accidental) return pitch;

  const SHARP_ORDER = ["F", "C", "G", "D", "A", "E", "B"];
  const FLAT_ORDER  = ["B", "E", "A", "D", "G", "C", "F"];

  let alteredLetters = [];

  if (keySignature.type === "sharps") {
    alteredLetters = SHARP_ORDER.slice(0, keySignature.count);
    if (alteredLetters.includes(letter)) {
      return `${letter}#${octave}`;
    }
  }

  if (keySignature.type === "flats") {
    alteredLetters = FLAT_ORDER.slice(0, keySignature.count);
    if (alteredLetters.includes(letter)) {
      return `${letter}b${octave}`;
    }
  }

  return pitch;
}

function applyDrawnAccidentals(pitch, drawnAccidentals) {
  if (!pitch || !drawnAccidentals?.[1]) return pitch;

  const match = pitch.match(/^([A-G])([#b]?)(\d)$/);
  if (!match) return pitch;

  const [, letter, accidental, octave] = match;

  // Explicit accidental always wins
  if (accidental) return pitch;

  // Find a drawn accidental that matches this letter
  const entry = drawnAccidentals[1].find(a =>
    a.pitch.startsWith(letter)
  );

  if (!entry) return pitch;

  return `${letter}${entry.accidental}${octave}`;
}

function formatGrandStaffFeedback(parts) {
  return parts.map(p => {
    let text = "";

    if (p.id === "staffSetup") {
      text = p.correct
        ? "Staff setup correct ✓"
        : "Staff setup incorrect";
    }

    else if (p.id === "keySignatureTreble") {
      text = p.correct
        ? `Treble key signature correct ✓`
        : `Treble key signature incorrect`;
    }

    else if (p.id === "keySignatureBass") {
      text = p.correct
        ? `Bass key signature correct ✓`
        : `Bass key signature incorrect`;
    }

    // ✅ FIXED: match "1-treble" / "1-bass"
    else if (p.id.includes("-treble") || p.id.includes("-bass")) {
      const [slot, staff] = p.id.split("-");

      const staffLabel =
        staff === "treble"
          ? "Treble"
          : "Bass";

      const expected =
        Array.isArray(p.expected)
          ? p.expected.join("–")
          : p.expected;

      const received =
        Array.isArray(p.received)
          ? p.received.join("–")
          : p.received ?? "—";

      text = p.correct
        ? `Measure ${slot} ${staffLabel} correct ✓`
        : `Measure ${slot} ${staffLabel} incorrect`;
    }

    else {
      text = p.correct ? "Correct ✓" : "Incorrect";
    }

    return {
      id: p.id,
      correct: p.correct,
      text
    };
  });
}

function LedgerLines({ pitch, clef }) {
  const index = pitchToStaffIndex(pitchForPosition(pitch), clef);
  const lw = 5;

  // Staff spans indices -1 .. 9
  if (index >= -1 && index <= 9) return null;

  // Map staff index → ledger line Y offsets (in px)
  const LEDGER_MAP = {
    "-2": [0],
    "-3": [-5],
    "-4": [0, -10],
    "-5": [-5, -15],

    "10": [0],
    "11": [5],
    "12": [0, 10],
    "13": [5, 15]
  };

  const ys = LEDGER_MAP[index];
  if (!ys) return null;

  return (
    <g>
      {ys.map((y, i) => (
        <line
          key={i}
          x1={-lw}
          x2={lw + 17}
          y1={y}
          y2={y}
          stroke="#000"
          strokeWidth={1}
        />
      ))}
    </g>
  );
}


/* ───────────── KEY SIGNATURE RENDER ───────────── */

const TREBLE_SHARPS = ["F#5","C#5","G#5","D#5","A#4","E#5","B#4"];
const TREBLE_FLATS  = ["Bb4","Eb5","Ab4","Db5","Gb4","Cb5","Fb4"];
const BASS_SHARPS   = ["F#3","C#3","G#3","D#3","A#2","E#3","B#2"];
const BASS_FLATS    = ["Bb2","Eb3","Ab2","Db3","Gb2","Cb3","Fb2"];

function renderKeySignatureFromAccidentals(accidentals, staff) {
  if (!accidentals || !accidentals[1]) return null;

  const staffTop =
    staff === "treble" ? TREBLE_STAFF_TOP : BASS_STAFF_TOP;

  return accidentals[1].map((a, i) => (
    <g
      key={`${staff}-ks-${i}`}
      transform={`translate(
        ${KEY_SIG_X_START + i * 10},
        ${pitchToY(a.pitch, staff, { staffTop })}
      )`}
    >
      <Glyph
        symbol="quarter_note"
        accidental={a.accidental}
        accidentalOnly
      />
    </g>
  ));
}


function renderKeySignature(keySig, staff) {
    if (!keySig || keySig.count === 0) return null;

    const pitches =
        staff === "treble"
        ? keySig.type === "sharps" ? TREBLE_SHARPS : TREBLE_FLATS
        : keySig.type === "sharps" ? BASS_SHARPS : BASS_FLATS;

    const staffTop = staff === "treble"
        ? TREBLE_STAFF_TOP
        : BASS_STAFF_TOP;

    return pitches.slice(0, keySig.count).map((pitch, i) => (
        <g
        key={`${staff}-${pitch}`}
        transform={`translate(
            ${KEY_SIG_X_START + i * 10},
            ${pitchToY(
            pitch,
            staff,
            { staffTop }
            )}
        )`}
        >
        <Glyph
            symbol="quarter_note"
            accidental={keySig.type === "sharps" ? "#" : "b"}
            accidentalOnly
        />
        </g>
    ));
    }

/* ───────────── COMPONENT ───────────── */

export default function GrandStaffInputWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const { allowedSymbols, measures, noteLimits = {} } = config;

  const slotCount = config.slotCount ?? measures;
  const slotsPerBar = config.slotsPerBar ?? 1;
  const isSelectionMode = config.inputMode === "selectLowerNote";
  const isMarkAccidentalsMode = config.inputMode === "markAccidentals";
  const isRomanNumeralAnalysisMode = config.inputMode === "romanNumeralAnalysis";
  const isBassAndRomanInputMode = config.inputMode === "bassAndRomanInput";
  const isCrossOutLabelsMode = config.inputMode === "crossOutLabels";

  function isInputSlot(slot) {
    if (!Array.isArray(config.inputSlots)) return true;
    return config.inputSlots.includes(slot);
  }

  function isLockedSlot(slot) {
    if (!Array.isArray(config.inputSlots)) return false;
    return !config.inputSlots.includes(slot);
  }

  const notePrompts = config.notePrompts || {};

  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedAccidental, setSelectedAccidental] = useState("");
  const [hover, setHover] = useState(null);
  const [showKeySigPalette, setShowKeySigPalette] = useState(false);
  const [activeTextField, setActiveTextField] = useState(null);
  // hover = { slot, staff, pitch }

  const keySignature = value.keySignature;

  // const staffWidth = LEFT_MARGIN + measures * SLOT_WIDTH + 20;
  // const END_BARLINE_X = LEFT_MARGIN + measures * SLOT_WIDTH + 20;

  const SLOT_WIDTH = config.slotWidth ?? DEFAULT_SLOT_WIDTH;
  const keySigMaxCount = getMaxKeySignatureCount(config, value);
  const firstSlotExtraOffset = getFirstSlotExtraOffset(keySigMaxCount);
  // const staffWidth = LEFT_MARGIN + slotCount * SLOT_WIDTH + 20;
  // const END_BARLINE_X = LEFT_MARGIN + slotCount * SLOT_WIDTH + 20;
  const staffWidth =
  LEFT_MARGIN + firstSlotExtraOffset + slotCount * SLOT_WIDTH + 20;

  const END_BARLINE_X =
  LEFT_MARGIN + firstSlotExtraOffset + slotCount * SLOT_WIDTH + 20; 

  const maxTrebleNotes = noteLimits.treble ?? 1;
  const maxBassNotes = noteLimits.bass ?? 1;

  const [showKeySigModal, setShowKeySigModal] = useState(false);
  const [draftKeySig, setDraftKeySig] = useState(value.keySignature || null);
  const [draftAccidentals, setDraftAccidentals] = useState({
    treble: {},
    bass: {}
  });

  const [showStaffSetupModal, setShowStaffSetupModal] = useState(false);

  const requireStaffSetup = !!config.requireStaffSetup;


  const staffSetup = value.staffSetup ??
  (requireStaffSetup
    ? { treble: false, bass: false, brace: false }
    : { treble: "treble", bass: "bass", brace: "brace" });

  // const staffSetup = requireStaffSetup
  //   ? value.staffSetup || { treble: false, bass: false, brace: false }
  //   : { treble: true, bass: true, brace: true };

  const staffReady =
    staffSetup.treble || staffSetup.bass || staffSetup.brace;

  const [draftStaffSetup, setDraftStaffSetup] = useState(staffSetup);

  /* ───────────── STAFF HELPERS ───────────── */
  function layoutOffset(layout, index) {
    if (layout !== "offset") return 0;
    return index === 0 ? -25 : 25;
  }

  function insertAccidental(value, accGlyph) {
    return (value || "") + accGlyph;
  }

  function clefForStaff(staff) {
    return staff === "treble" ? "treble" : "bass";
  }

  function staffTop(staff) {
    return staff === "treble"
      ? TREBLE_STAFF_TOP
      : BASS_STAFF_TOP;
  }

  function staffFromY(y) {
    if (
      y >= -10 &&
      y <= TREBLE_STAFF_TOP + STAFF_HEIGHT + STAFF_GAP / 2 - 10
    ) {
      return "treble";
    }

    if (
      y >= BASS_STAFF_TOP - LEDGER_ALLOWANCE &&
      y <= BASS_STAFF_TOP + STAFF_HEIGHT + LEDGER_ALLOWANCE
    ) {
      return "bass";
    }

    return null;
  }

  function snapAtY(y, staff) {
    return snapYToPitch(
      y,
      clefForStaff(staff),
      { staffTop: staffTop(staff) }
    );
  }

  function setKeySignature(type, count) {
    onChange({
      ...value,
      keySignature: count === 0 ? null : { type, count }
    });
    setShowKeySigPalette(false);
  }

  function accidentalFromResolvedPitch(resolvedPitch) {
    if (resolvedPitch.includes("#")) return "#";
    if (resolvedPitch.includes("b")) return "b";
    return null;
  }

  function accidentalForRendering(pitch, keySignature, explicitAccidental) {
    // explicit accidental always wins
    if (explicitAccidental) {
      if (explicitAccidental === "♮") return "♮";
      return explicitAccidental;
    }

    if (!keySignature) return null;

    const match = pitch.match(/^([A-G])(\d)$/);
    if (!match) return null;

    const letter = match[1];

    const SHARP_ORDER = ["F", "C", "G", "D", "A", "E", "B"];
    const FLAT_ORDER  = ["B", "E", "A", "D", "G", "C", "F"];

    const alteredLetters =
      keySignature.type === "sharps"
        ? SHARP_ORDER.slice(0, keySignature.count)
        : FLAT_ORDER.slice(0, keySignature.count);

    // Key signature already accounts for this pitch → no accidental
    if (alteredLetters.includes(letter)) {
      return null;
    }

    return null;
  }

  function shouldRenderBarlineAfterSlot(slot) {
    if (config.renderBarlines === false) return false;

    if (config.slotsPerBar) {
      return slot % slotsPerBar === 0 && slot < slotCount;
    }

    return slot < measures;
  }

  function handleMarkAccidental(slot, staff, pitch) {
    if (mode !== "edit") return;
    if (!isMarkAccidentalsMode) return;

    onChange({
      ...value,
      markedAccidentals: {
        ...(value.markedAccidentals || {}),
        [slot]: {
          ...(value.markedAccidentals?.[slot] || {}),
          [staff]: {
            ...(value.markedAccidentals?.[slot]?.[staff] || {}),
            [pitch]: selectedAccidental
          }
        }
      }
    });
  }

  function toggleCrossOutLabel(slot) {
  if (mode !== "edit") return;

  onChange({
    ...value,
    crossedOutLabels: {
      ...(value.crossedOutLabels || {}),
      [slot]: !value.crossedOutLabels?.[slot]
    }
  });
}

  function markedAccidentalFor(slot, staff, pitch, fallback) {
    const marked =
      value.markedAccidentals?.[slot]?.[staff]?.[pitch];

    if (marked === "") return null;
    if (marked !== undefined) return marked;

    return fallback;
  }

  function handleSelectSlot(slot) {
    if (mode !== "edit") return;
    if (!isSelectionMode) return;

    const pair = Math.ceil(slot / slotsPerBar);

    onChange({
      ...value,
      selections: {
        ...(value.selections || {}),
        [pair]: slot
      }
    });
  }

  /* ───────────── CLICK HANDLER (UPDATED) ───────────── */

  function handleClick(slot, y) {
    if (mode !== "edit") return;
    if (config.inputMode === "typeNames") return;
    if (isCrossOutLabelsMode) return;
    if (isLockedSlot(slot)) return;
    if (requireStaffSetup && !staffReady) {
      return;
    }
    if (!selectedSymbol) return;

    const staff = staffFromY(y);
    if (!staff) return;

    const rawPitch = snapAtY(y, staff);

    const pitch = applyDrawnAccidentals(
      rawPitch,
      value.keySignature?.[staff]
    );

    const isTypeNames = config.inputMode === "typeNames";
    const given = config.givenNotes?.[slot];

    // const slotData = value[slot] || { treble: [], bass: [] };
    // const notes = slotData[staff];
    const slotData = {
      treble: value[slot]?.treble || [],
      bass: value[slot]?.bass || []
    };

    const notes = slotData[staff] || [];

    // remove note if pitch already exists
    const existingIndex = notes.findIndex(
      n => n.pitch === pitch
    );

    if (existingIndex !== -1) {
      const nextNotes = [...notes];
      nextNotes.splice(existingIndex, 1);

      onChange({
        ...value,
        [slot]: {
          ...slotData,
          [staff]: nextNotes
        }
      });
      return;
    }

    // enforce limits
    const maxNotes =
    staff === "treble" ? maxTrebleNotes : maxBassNotes;
    if (notes.length >= maxNotes) return;

    onChange({
      ...value,
      [slot]: {
        ...slotData,
        [staff]: [
          ...notes,
          {
            pitch,
            symbol: selectedSymbol,
            stemUp: !selectedSymbol.startsWith("whole"),
            accidental:
              config.accidentalInputMode === "palette"
                ? selectedAccidental
                : accidentalFromPitch(pitch)
          }
        ]
      }
    });
  }

  function AnalysisButtonGroup({
    field,
    choices,
    feedbackKey,
    fontFamily = "inherit"
  }) {
    return (
      <div style={{ marginBottom: 8 }}>
        {choices.map((choice) => {
          const currentValue =
            field === "romanNumeral"
              ? value.choice
              : value[field];

          const isSelected = currentValue === choice;

          const border =
            mode === "review" && isSelected
              ? feedback?.parts?.[feedbackKey]?.correct
                ? "2px solid #2e7d32"
                : "2px solid #c62828"
              : isSelected
              ? "2px solid #1976d2"
              : "1px solid #aaa";

          return (
            <button
              key={choice}
              disabled={mode !== "edit"}
              onClick={() => {
                if (field === "romanNumeral") {
                  onChange({
                    ...value,
                    choice
                  });
                } else {
                  onChange({
                    ...value,
                    [field]: choice
                  });
                }
              }}
              style={{
                marginRight: 8,
                padding: "6px 14px",
                fontSize: 16,
                cursor: mode === "edit" ? "pointer" : "default",
                background: isSelected ? "#e3f2fd" : "#fff",
                border,
                borderRadius: 4,
                fontFamily
              }}
            >
              {choice}
            </button>
          );
        })}
      </div>
    );
  }

  function updatePostStaffAnswer(questionId, answerValue) {
  onChange({
    ...value,
    postStaffAnswers: {
      ...(value.postStaffAnswers || {}),
      [questionId]: answerValue
    }
  });
}

function feedbackPart(id, legacyKey = null) {
  if (Array.isArray(feedback?.parts)) {
    return feedback.parts.find(p => p.id === id) || null;
  }

  return (
    feedback?.parts?.[id] ||
    (legacyKey != null ? feedback?.parts?.[legacyKey] : null) ||
    null
  );
}

function crossOutFeedback(slot) {
  return feedbackPart(`crossOutLabel-${slot}`);
}

function PostStaffQuestions() {
  if (!Array.isArray(config.postStaffQuestions)) return null;

  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: 0,
        display: "flex",
        flexDirection: "column",
        gap: 10
      }}
    >
      {config.postStaffQuestions.map((q, index) => {
        const selected = value.postStaffAnswers?.[q.id] || "";
        const label = q.label ?? String.fromCharCode(65 + index);

        const partFeedback = feedbackPart(`postStaff-${q.id}`, q.id);

        return (
          <div
            key={q.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap"
            }}
          >
            {label&& <div style={{ fontWeight: 700 }}>
              {label}.
            </div>}

            <div>{q.text}</div>

            <div style={{ display: "flex", gap: 8 }}>
              {(q.choices || []).map(choice => {
                const choiceValue =
                  typeof choice === "string"
                    ? choice
                    : choice.value;

                const choiceLabel =
                  typeof choice === "string"
                    ? choice
                    : choice.label;

                const isSelected = selected === choiceValue;

                const border =
                  mode === "review" && isSelected
                    ? partFeedback?.correct
                      ? "2px solid #2e7d32"
                      : "2px solid #c62828"
                    : isSelected
                    ? "2px solid #1976d2"
                    : "1px solid #aaa";

                return (
                  <button
                    key={choiceValue}
                    type="button"
                    disabled={mode !== "edit"}
                    onClick={() =>
                      updatePostStaffAnswer(q.id, choiceValue)
                    }
                    style={{
                      padding: "6px 12px",
                      fontSize: 16,
                      borderRadius: 6,
                      border,
                      background: isSelected ? "#e3f2fd" : "#fff",
                      cursor: mode === "edit" ? "pointer" : "default"
                    }}
                  >
                    {choiceLabel}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      <div style={{position: "relative"}}>
        {/* KEY SIGNATURE BUTTON */}
        {mode === "edit" && config.requestedKeySignature && (
          <div style={{ marginBottom: 8, fontSize: '1.25em' }}>
            <button
              style={{background:'lightgrey'}}
              onClick={() => {
                setDraftKeySig(value.keySignature || null);
                setShowKeySigModal(true);
              }}
            >
              Draw key signature
            </button>
          </div>
        )}

        {requireStaffSetup && mode === "edit" && (
          <div style={{ marginBottom: 8, fontSize: '1.25em' }}>
            <button
              style={{background:'lightgrey'}}
              onClick={() => {
                setDraftStaffSetup(staffSetup);
                setShowStaffSetupModal(true);
              }}
            >
              Set up staff
            </button>
          </div>
        )}

        {showStaffSetupModal && (
  <>
    {/* backdrop (scrollable) */}
    <div
      onClick={() => setShowStaffSetupModal(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        // overflowY: "auto",              // ✅ allows scroll
        padding: "40px 16px",
        zIndex: 999
      }}
    >
      {/* modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          padding: 16,
          borderRadius: 8,
          maxWidth: 500,
          margin: "0 auto",             // ✅ horizontal centering
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",            // ✅ internal scroll
          overflowX: "hidden",
          boxShadow: "0 12px 30px rgba(0,0,0,0.25)"
        }}
      >
        <GrandStaffSetupWidget
          config={{
            number: "1A",
            prompt: {
              type: "blocks",
              blocks: [
                {
                  type: "paragraph",
                  content: [
                    { bold: "Click" },
                    " the boxes, then choose symbols to complete the staff."
                  ]
                }
              ]
            },
            points: 1.5
          }}
          value={{ staffSetup: draftStaffSetup }}
          onChange={(v) => {
            setDraftStaffSetup(v.staffSetup);
          }}
          mode="edit"
        />

        {/* action buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 12
          }}
        >
          <button onClick={() => setShowStaffSetupModal(false)}>
            Cancel
          </button>

          <button
            onClick={() => {
              onChange({
                ...value,
                staffSetup: draftStaffSetup
              });
              setShowStaffSetupModal(false);
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </>
)}


        {showKeySigModal && (
  <>
    {/* backdrop */}
    <div
      onClick={() => setShowKeySigModal(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 999
      }}
    />

    {/* modal */}
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        inset: "50px 0",                 // ⬅ vertical breathing room
        background: "white",
        padding: 16,
        borderRadius: 8,
        maxWidth: 520,
        margin: "0 auto",               // ✅ horizontal centering
        maxHeight: "calc(100vh - 100px)",
        overflowY: "auto",              // ✅ internal scroll
        overflowX: "hidden",
        zIndex: 1000,
        boxShadow: "0 12px 30px rgba(0,0,0,0.25)"
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <KeySignatureInputWidget
          config={{
            clef: "treble",
            slots: 1,
            prompt: "Draw "+config.requestedKeySignature,
            number: config.number + "-Treble",
            points: config.keySigPoints ? config.keySigPoints : 0.5,
            allowLedgerLines: false
          }}
          value={{ accidentals: draftAccidentals.treble }}
          onChange={(v) =>
            setDraftAccidentals((d) => ({
              ...d,
              treble: v.accidentals || {}
            }))
          }
          mode="edit"
        />

        <KeySignatureInputWidget
          config={{
            clef: "bass",
            slots: 1,
            prompt: "Draw "+config.requestedKeySignature,
            number: config.number + "-Bass",
            points: config.keySigPoints ? config.keySigPoints : 0.5,
            allowLedgerLines: false
          }}
          value={{ accidentals: draftAccidentals.bass }}
          onChange={(v) =>
            setDraftAccidentals((d) => ({
              ...d,
              bass: v.accidentals || {}
            }))
          }
          mode="edit"
        />
      </div>

      {/* action buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 12
        }}
      >
        <button onClick={() => setShowKeySigModal(false)}>
          Cancel
        </button>

        <button
          onClick={() => {
            onChange({
              ...value,
              keySignature: {
                treble: draftAccidentals.treble,
                bass: draftAccidentals.bass
              }
            });
            setShowKeySigModal(false);
          }}
        >
          Save
        </button>
      </div>
    </div>
  </>
)}


        {/* KEY SIGNATURE PALETTE */}
        {showKeySigPalette && (
        <>
            {/* click-outside backdrop */}
            <div
            onClick={() => setShowKeySigPalette(false)}
            style={{
                position: "absolute",
                inset: 0,
                zIndex: 998
            }}
            />

            {/* floating palette */}
            <div
            style={{
                position: "absolute",
                top: 20,
                left: "50%",
                transform: "translateX(-50%)",
                background: "white",
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: 10,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                zIndex: 999,
                display: "flex",
                flexDirection: "column",
                gap: 6
            }}
            >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Draw key signature
            </div>

            <button onClick={() => setKeySignature("sharps", 0)}>
                No key
            </button>

            <div style={{ display: "flex", gap: 4 }}>
                {Array.from({ length: 7 }).map((_, i) => (
                <button
                    key={`sharp-${i}`}
                    onClick={() => setKeySignature("sharps", i + 1)}
                >
                    {i + 1} ♯
                </button>
                ))}
            </div>

            <div style={{ display: "flex", gap: 4 }}>
                {Array.from({ length: 7 }).map((_, i) => (
                <button
                    key={`flat-${i}`}
                    onClick={() => setKeySignature("flats", i + 1)}
                >
                    {i + 1} ♭
                </button>
                ))}
            </div>
            </div>
        </>
        )}


        {/* {config.inputMode !== "typeNames" &&
        !isSelectionMode &&
        allowedSymbols?.length > 0 && (
          <SymbolPalette
            allowedSymbols={allowedSymbols}
            selected={selectedSymbol}
            onSelect={setSelectedSymbol}
          />
        )} */}
        {config.inputMode !== "typeNames" &&
          !isSelectionMode &&
          !isRomanNumeralAnalysisMode &&
          !isCrossOutLabelsMode &&
          (allowedSymbols?.length > 0 || isMarkAccidentalsMode) && (
            <div>
              {allowedSymbols?.length > 0 && (
                <SymbolPalette
                  allowedSymbols={allowedSymbols}
                  selected={selectedSymbol}
                  onSelect={setSelectedSymbol}
                />
              )}

              {(config.accidentalInputMode === "palette" ||
                isMarkAccidentalsMode) && (
                <AccidentalPalette
                  selected={selectedAccidental}
                  onSelect={setSelectedAccidental}
                  showInstruction={
                    mode === "edit" &&
                    config.accidentalInputMode === "palette" &&
                    !isMarkAccidentalsMode
                  }
                />
              )}
            </div>
        )}

        {config.requestedKeySignature && (
        <div
            style={{
            position: "absolute",
            left: LEFT_MARGIN + SLOT_WIDTH / 2,
            top: TREBLE_STAFF_TOP + LEDGER_ALLOWANCE,
            transform: "translateX(-50%)",
            fontWeight: 600,
            pointerEvents: "none"
            }}
        >
            {config.requestedKeySignature}
        </div>
        )}


        <svg
          width={staffWidth}
          height={BASS_STAFF_TOP + STAFF_HEIGHT + LEDGER_ALLOWANCE + ((config.selectChoices || config.selectChoicesBySlot)?50:0) + ((config.requestedRomanNumerals || config.inputMode=="bassAndRomanInput")?60:0) + ((config.inputMode=="typeNames"?60:0)) + (config.inputMode === "crossOutLabels"?50:0)}
        >
          {/* STAFF LINES */}
          {renderStaffLines(staffWidth, TREBLE_STAFF_TOP)}
          {renderStaffLines(staffWidth, BASS_STAFF_TOP)}

          {/* ───────────── SCALE LABEL (LEFT OF STAFF) ───────────── */}
          {config.scaleLabel && (
            <text
              x={SCALE_LABEL_X}
              y={SCALE_LABEL_Y}
              textAnchor="end"
              fontSize="16"
              fontWeight="bold"
            >
              {config.scaleLabel}
            </text>
          )}

          {/* BEGINNING BARLINE */}
            <line
            x1={50}
            x2={50}
            y1={SYSTEM_TOP}
            y2={SYSTEM_BOTTOM-20}
            stroke="#000"
            strokeWidth={1.6}
            />

            {/* GRAND STAFF BRACE */}
            {/* <g
            transform={`
                matrix(
                0.034, 0, 0, 0.034,
                ${LEFT_MARGIN - 55},
                ${TREBLE_STAFF_TOP + 75}
                )
            `}
            >
            <path
                d={BRACE_PATH}
                fill="#000"
                stroke="#000"
                strokeWidth={1.2}
            />
            </g> */}



          {/* CLEFS */}
          {/* {renderClef("treble", TREBLE_STAFF_TOP)}
          {renderClef("bass", BASS_STAFF_TOP)} */}

          {staffSetup.treble === "treble" && renderClef("treble", TREBLE_STAFF_TOP)}
          {staffSetup.treble === "bass" && renderClef("bass", TREBLE_STAFF_TOP)}
          {staffSetup.treble === "brace" && (
            <g
              transform={`
                matrix(
                  0.02, 0, 0, 0.02,
                  ${LEFT_MARGIN - 27},
                  ${TREBLE_STAFF_TOP + 20}
                )
              `}
            >
              <path d={BRACE_PATH} fill="#000" />
            </g>
          )}




          {staffSetup.bass === "bass" && renderClef("bass", BASS_STAFF_TOP)}
          {staffSetup.bass === "treble" && renderClef("treble", BASS_STAFF_TOP)}
          {staffSetup.bass === "brace" && (
            <g
              transform={`
                matrix(
                  0.02, 0, 0, 0.02,
                  ${LEFT_MARGIN - 27},
                  ${BASS_STAFF_TOP + 20}
                )
              `}
            >
              <path d={BRACE_PATH} fill="#000" />
            </g>
          )}


          {staffSetup.brace === "brace" && (
                      <g
                        transform={`
                          matrix(
                            0.034, 0, 0, 0.034,
                            ${LEFT_MARGIN - 55},
                            ${TREBLE_STAFF_TOP + 75}
                          )
                        `}
                      >
                        <path d={BRACE_PATH} fill="#000" />
                      </g>
                    )}
                    {staffSetup.brace === "bass" &&
                      renderClef("bass", BASS_STAFF_TOP - 50, -40)}
                    {staffSetup.brace === "treble" &&
                      renderClef("treble", BASS_STAFF_TOP - 57, -40)}



          {/* {renderKeySignature(value.keySignature?.treble, "treble")}
          {renderKeySignature(value.keySignature?.bass,   "bass")} */}
          
          {/* {renderKeySignatureFromAccidentals(value.keySignature?.treble, "treble")}
          {renderKeySignatureFromAccidentals(value.keySignature?.bass, "bass")} */}
          {renderKeySignatureFromAccidentals(
            config.keySignature?.treble || value.keySignature?.treble,
            "treble"
          )}
          {renderKeySignatureFromAccidentals(
            config.keySignature?.bass || value.keySignature?.bass,
            "bass"
          )}

          {/* SLOTS */}
          {Array.from({ length: slotCount }).map((_, i) => {
            const slot = i + 1;
            const partIndex = slot - 1;
            const x = LEFT_MARGIN + firstSlotExtraOffset + i * SLOT_WIDTH;
            const pair = Math.ceil(slot / slotsPerBar);
            const isSelected =
              isSelectionMode && value.selections?.[pair] === slot;
            const given = config.givenNotes?.[slot];
            const isTypeNames = config.inputMode === "typeNames";

            // const slotData = given
            //   ? {
            //       treble: (given.treble?.notes || []).map(pitch => ({
            //         pitch,
            //         symbol: given.symbol,
            //         stemUp: false,
            //         accidental: accidentalFromPitch(pitch)
            //       })),
            //       bass: (given.bass?.notes || []).map(pitch => ({
            //         pitch,
            //         symbol: given.symbol,
            //         stemUp: true,
            //         accidental: accidentalFromPitch(pitch)
            //       }))
            //     }
            //   : value[slot] || { treble: [], bass: [] };
            const givenSlotData = {
              treble: (given?.treble?.notes || []).map(pitch => ({
                pitch,
                symbol: given.symbol,
                stemUp: false,
                accidental: accidentalFromPitch(pitch),
                isGiven: true
              })),
              bass: (given?.bass?.notes || []).map(pitch => ({
                pitch,
                symbol: given.symbol,
                stemUp: true,
                accidental: accidentalFromPitch(pitch),
                isGiven: true
              }))
            };

            const studentSlotData = {
              treble: value[slot]?.treble || [],
              bass: value[slot]?.bass || []
            };

            const slotData = {
              treble: [...givenSlotData.treble, ...studentSlotData.treble],
              bass: [...givenSlotData.bass, ...studentSlotData.bass]
            };

            // const layoutByStaff = {
            //   treble: layoutChordNotes({
            //     slot,
            //     clef: "treble",
            //     value,
            //     givenPitches: given?.treble?.notes || [],
            //     entries: given ? [] : slotData.treble,
            //     skipAccidentalLayout: given?.treble?.layout === "offset"
            //   }),

            //   bass: layoutChordNotes({
            //     slot,
            //     clef: "bass",
            //     value,
            //     givenPitches: given?.bass?.notes || [],
            //     entries: given ? [] : slotData.bass,
            //     skipAccidentalLayout: given?.bass?.layout === "offset"
            //   })
            // };
            const layoutByStaff = {
              treble: layoutChordNotes({
                slot,
                clef: "treble",
                value,
                givenPitches: given?.treble?.notes || [],
                entries: studentSlotData.treble,
                keySignature:
                  config.keySignature?.treble || value.keySignature?.treble,
                skipAccidentalLayout: given?.treble?.layout === "offset"
              }),

              bass: layoutChordNotes({
                slot,
                clef: "bass",
                value,
                givenPitches: given?.bass?.notes || [],
                entries: studentSlotData.bass,
                keySignature:
                  config.keySignature?.bass || value.keySignature?.bass,
                skipAccidentalLayout: given?.bass?.layout === "offset"
              })
            };

            return (
              <g key={slot}>
                {/* PLACED NOTES */}
                {["treble", "bass"].map(staff =>
                  slotData[staff].map((note, idx) => {
                    // const noteLayout =
                    //   layoutByStaff[staff]?.[
                    //     given ? `given-${idx}` : `entry-${idx}`
                    //   ];

                    // const dx =
                    //   given?.[staff]?.layout === "offset"
                    //     ? layoutOffset("offset", idx)
                    //     : noteLayout?.noteOffsetX ?? 0;
                  const noteLayoutId = note.isGiven
                    ? `given-${idx}`
                    : `entry-${idx - givenSlotData[staff].length}`;

                  const noteLayout = layoutByStaff[staff]?.[noteLayoutId];

                  const dx =
                    note.isGiven && given?.[staff]?.layout === "offset"
                      ? layoutOffset("offset", idx)
                      : noteLayout?.noteOffsetX ?? 0;

                    return (
                      <g
                        key={`${staff}-${idx}`}
                        transform={`translate(
                          ${x + SLOT_WIDTH / 2 + dx},
                          ${pitchToY(
                            pitchForPosition(note.pitch),
                            clefForStaff(staff),
                            { staffTop: staffTop(staff) }
                          )}
                        )`}
                        style={{
                          cursor: isMarkAccidentalsMode ? "pointer" : "default"
                        }}
                        onClick={(e) => {
                          if (!isMarkAccidentalsMode) return;
                          e.stopPropagation();
                          handleMarkAccidental(slot, staff, note.pitch);
                        }}
                      >
                        <rect
                          x={-12}
                          y={-18}
                          width={36}
                          height={36}
                          fill="transparent"
                        />
                        <LedgerLines
                          pitch={note.pitch}
                          clef={clefForStaff(staff)}
                        />
                        <Glyph
                          symbol={note.symbol}
                          stemUp={note.stemUp}
                          fill={isSelected ? "#1976d2" : "#000"}
                          accidental={markedAccidentalFor(
                            slot,
                            staff,
                            note.pitch,
                            noteLayout?.accidental
                          )}
                          accidentalOffsetX={noteLayout?.accidentalOffsetX}
                        />
                        {isSelected && (
                          <circle
                            cx={8}
                            cy={0}
                            r={18}
                            fill="none"
                            stroke="#1976d2"
                            strokeWidth={2}
                            pointerEvents="none"
                          />
                        )}
                      </g>
                    );
                  })
                )}

                {/* HOVER GHOST */}
                {config.inputMode !== "typeNames" &&
                  !isSelectionMode &&
                  isInputSlot(slot) &&
                  hover?.slot === slot &&
                  selectedSymbol &&
                  (slotData[hover.staff]?.length ?? 0) <
                    (hover.staff === "treble" ? maxTrebleNotes : maxBassNotes) && (
                    <g
                      transform={`translate(
                        ${x + SLOT_WIDTH / 2},
                        ${pitchToY(
                          hover.pitch,
                          clefForStaff(hover.staff),
                          { staffTop: staffTop(hover.staff) }
                        )}
                      )`}
                      opacity={0.5}
                    >
                      <LedgerLines
                        pitch={hover.pitch}
                        clef={clefForStaff(hover.staff)}
                      />
                      <Glyph
                        symbol={selectedSymbol}
                        fill="#999"
                      />
                    </g>
                  )}

                {/* NOTE PROMPTS (from config) */}
                {config.notePrompts?.[slot]?.length > 0 && (
                <text
                    x={x + SLOT_WIDTH / 2 + 8}
                    y={BASS_STAFF_TOP + STAFF_HEIGHT + 26}
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight={600}
                    fill="#000"
                    pointerEvents="none"
                >
                    {config.notePrompts[slot].join(" – ")}
                </text>
                )}

                {isCrossOutLabelsMode && config.noteLabels?.[slot] && (
  <foreignObject
    x={x + SLOT_WIDTH / 2 - 27}
    y={BASS_STAFF_TOP + STAFF_HEIGHT + 24}
    width={70}
    height={42}
  >
    <button
      xmlns="http://www.w3.org/1999/xhtml"
      type="button"
      disabled={mode !== "edit"}
      onClick={() => toggleCrossOutLabel(slot)}
      style={{
        position: "relative",
        width: "100%",
        height: "40px",
        fontSize: 26,
        fontWeight: 600,
        display:"flex",
        outline: "none",
        justifyContent:"center",
        alignItems:"center",
        background: "transparent",
        border:
          mode === "review"
            ? crossOutFeedback(slot)?.correct
              ? "2px solid #2e7d32"
              : "2px solid #c62828"
            : "1px solid transparent",
        borderRadius: 4,
        cursor: mode === "edit" ? "pointer" : "default"
      }}
    >
      {config.noteLabels[slot]}

      {value.crossedOutLabels?.[slot] && (
        <><span
          style={{
            position: "absolute",
            left: 8,
            right: 8,
            top: "50%",
            borderTop: "3px solid #000000",
            transform: "rotate(-25deg)",
            pointerEvents: "none"
          }}
        />
        <span
          style={{
            position: "absolute",
            left: 8,
            right: 8,
            top: "50%",
            borderTop: "3px solid #000000",
            transform: "rotate(25deg)",
            pointerEvents: "none"
          }}
        /></>
      )}
    </button>
  </foreignObject>
)}

                {/* SINGLE BARLINE AFTER MEASURE (except last) */}
                {/* {shouldRenderBarlineAfterSlot(slot) && (
                  <line
                    x1={x + SLOT_WIDTH}
                    x2={x + SLOT_WIDTH}
                    y1={SYSTEM_TOP}
                    y2={SYSTEM_BOTTOM - 20}
                    stroke="#000"
                    strokeWidth={1.2}
                    pointerEvents="none"
                  />
                )} */}
                {shouldRenderBarlineAfterSlot(slot) && (
                  config.doubleBarlineAfterSlots?.includes(slot) ? (
                    <g pointerEvents="none">
                      <line
                        x1={x + SLOT_WIDTH - 3}
                        x2={x + SLOT_WIDTH - 3}
                        y1={SYSTEM_TOP}
                        y2={SYSTEM_BOTTOM - 20}
                        stroke="#000"
                        strokeWidth={1.2}
                      />
                      <line
                        x1={x + SLOT_WIDTH + 1}
                        x2={x + SLOT_WIDTH + 1}
                        y1={SYSTEM_TOP}
                        y2={SYSTEM_BOTTOM - 20}
                        stroke="#000"
                        strokeWidth={1.2}
                      />
                    </g>
                  ) : (
                    <line
                      x1={x + SLOT_WIDTH}
                      x2={x + SLOT_WIDTH}
                      y1={SYSTEM_TOP}
                      y2={SYSTEM_BOTTOM - 20}
                      stroke="#000"
                      strokeWidth={1.2}
                      pointerEvents="none"
                    />
                  )
                )}

                {/* HIT AREA */}
                <rect
                  x={x}
                  y={TREBLE_STAFF_TOP - LEDGER_ALLOWANCE}
                  width={SLOT_WIDTH}
                  height={
                    STAFF_HEIGHT * 2 +
                    STAFF_GAP +
                    LEDGER_ALLOWANCE * 2
                  }
                  fill="transparent"
                  onMouseMove={(e) => {
                    if (mode !== "edit") return;
                    if (isSelectionMode || isMarkAccidentalsMode || isRomanNumeralAnalysisMode) return;
                    if (isLockedSlot(slot)) return;
                    const svg = e.currentTarget.ownerSVGElement;
                    const pt = svg.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const y =
                      pt.matrixTransform(
                        svg.getScreenCTM().inverse()
                      ).y;

                    const staff = staffFromY(y);
                    if (!staff) {
                      setHover(null);
                      return;
                    }

                    setHover({
                      slot,
                      staff,
                      pitch: snapAtY(y, staff)
                    });
                  }}
                  onMouseLeave={() => setHover(null)}
                  pointerEvents={isMarkAccidentalsMode ? "none" : "auto"}
                  onClick={(e) => {
                    if (mode !== "edit") return;
                    const svg = e.currentTarget.ownerSVGElement;
                    const pt = svg.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const y =
                      pt.matrixTransform(
                        svg.getScreenCTM().inverse()
                      ).y;

                    // handleClick(slot, y);
                    if (isSelectionMode) {
                      handleSelectSlot(slot);
                    } else if (isMarkAccidentalsMode || isRomanNumeralAnalysisMode) {
                      return;
                    } else {
                      handleClick(slot, y);
                    }
                  }}
                />

                {(config.inputMode === "typeNames" ||
                config.inputMode === "bassAndRomanInput") &&
                config.givenNotes?.[slot] && (
                  <foreignObject
                    x={x + SLOT_WIDTH / 2 - 16}
                    y={BASS_STAFF_TOP + STAFF_HEIGHT + 32}
                    width={90}
                    height={76}
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{ position: "relative" }}
                    >
                      <input
                        value={value.typed?.[slot] || ""}
                        disabled={mode !== "edit"}
                        onFocus={() => setActiveTextField(`typed:${slot}`)}
                        onBlur={() => setActiveTextField(null)}
                        onChange={(e) =>
                          onChange({
                            ...value,
                            typed: {
                              ...(value.typed || {}),
                              [slot]: isBassAndRomanInputMode
                                ? e.target.value
                                : e.target.value.toUpperCase()
                            }
                          })
                        }
                        style={{
                          width: "42px",
                          height: "32px",
                          textAlign: "center",
                          fontSize: "16px",
                          borderRadius: "4px",
                          border:
                            mode === "review"
                              ? feedbackPart(
                                  isBassAndRomanInputMode
                                    ? `romanNumeral-${slot}`
                                    : `typed-${slot}`,
                                  slot
                                )?.correct
                                ? "2px solid #2e7d32"
                                : "2px solid #c62828"
                              : "1px solid #aaa"
                        }}
                      />

                      {!isBassAndRomanInputMode && activeTextField === `typed:${slot}` && mode === "edit" && (
                        <div
                          style={{
                            position: "absolute",
                            top: 38,
                            left: 0,
                            zIndex: 20
                          }}
                        >
                          <AccidentalButtons
                            onSelect={(acc) =>
                              onChange({
                                ...value,
                                typed: {
                                  ...(value.typed || {}),
                                  [slot]: insertAccidental(
                                    value.typed?.[slot] || "",
                                    acc
                                  )
                                }
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  </foreignObject>
                )}

                {/* Selector */}
{(Array.isArray(config.selectChoices) ||
  Array.isArray(config.selectChoicesBySlot?.[slot])) &&
  isInputSlot(slot) &&
  config.inputMode !== "typeNames" &&
  !isRomanNumeralAnalysisMode &&
  (
    config.selectChoicesBySlot?.[slot] ? (
      (() => {
        const choices = config.selectChoicesBySlot[slot];
        const buttonWidth = 35;
        const buttonHeight = 35;
        const gap = 6;

        const totalWidth =
          choices.length * buttonWidth +
          (choices.length - 1) * gap;

        return (
          <g
            transform={`translate(${x + SLOT_WIDTH / 2}, ${
              BASS_STAFF_TOP + STAFF_HEIGHT + 18
            })`}
          >
            {choices.map((choice, index) => {
              const isSelected =
                value.choices?.[slot] === choice;

              const border =
                mode === "review" && isSelected
                  ? feedback?.parts?.[partIndex]?.correct
                    ? "2px solid #2e7d32"
                    : "2px solid #c62828"
                  : isSelected
                  ? "2px solid #1976d2"
                  : "1px solid #aaa";

              return (
                <foreignObject
                  key={choice}
                  x={
                    -totalWidth / 2 +
                    index * (buttonWidth + gap)
                  }
                  y={0}
                  width={buttonWidth}
                  height={buttonHeight}
                >
                  <button
                    xmlns="http://www.w3.org/1999/xhtml"
                    type="button"
                    disabled={mode !== "edit"}
                    onClick={() =>
                      onChange({
                        ...value,
                        choices: {
                          ...(value.choices || {}),
                          [slot]: choice
                        }
                      })
                    }
                    style={{
                      width: "100%",
                      padding: 0,
                      height: "100%",
                      fontSize: 18,
                      fontFamily: "myFirstFont",
                      background: isSelected
                        ? "#e3f2fd"
                        : "#fff",
                      border,
                      borderRadius: 4,
                      cursor:
                        mode === "edit"
                          ? "pointer"
                          : "default"
                    }}
                  >
                    {choice}
                  </button>
                </foreignObject>
              );
            })}
          </g>
        );
      })()
    ) : (
      <foreignObject
        x={x + SLOT_WIDTH / 2 - 25}
        y={BASS_STAFF_TOP + STAFF_HEIGHT + 15}
        width={60}
        height={34}
      >
        <div xmlns="http://www.w3.org/1999/xhtml">
          <select
            value={value.choices?.[slot] || ""}
            disabled={mode !== "edit"}
            onChange={(e) =>
              onChange({
                ...value,
                choices: {
                  ...(value.choices || {}),
                  [slot]: e.target.value
                }
              })
            }
            style={{
              width: "100%",
              height: "30px",
              fontSize: "14px",
              textAlign: "center",
              borderRadius: "4px",
              border:
                mode === "review"
                  ? feedback?.parts?.[partIndex]?.correct
                    ? "2px solid #2e7d32"
                    : "2px solid #c62828"
                  : "1px solid #aaa"
            }}
          >
            <option value="">—</option>
            {config.selectChoices.map(choice => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        </div>
      </foreignObject>
    )
)}

              </g>
            );
          })}

          {/* FINAL DOUBLE BARLINE */}
          <g>
            {/* thin bar */}
            <line
              x1={END_BARLINE_X - 6}
              x2={END_BARLINE_X - 6}
              y1={SYSTEM_TOP}
              y2={SYSTEM_BOTTOM - 20}
              stroke="#000"
              strokeWidth={1.2}
            />

            {/* thick bar (slightly to the right) */}
            <line
              x1={END_BARLINE_X - 1.5}
              x2={END_BARLINE_X - 1.5}
              y1={SYSTEM_TOP}
              y2={SYSTEM_BOTTOM - 20}
              stroke="#000"
              strokeWidth={3}
            />
          </g>

        </svg>

        {isRomanNumeralAnalysisMode && (
          <>
            {/* KEY TEXT INPUT — COPIED FROM STAFFINPUT typeNames */}
            <div
              style={{
                marginTop: 0,
                marginBottom: 10,
                marginLeft: LEFT_MARGIN,
                position: "relative",
                // Lift the key and its accidental popup together, reserving
                // the original row so the RN choices below stay in place.
                top: -40
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6
                }}
              >
                {/* INPUT + ACCIDENTALS ANCHOR */}
                <div style={{ position: "relative" }}>
                  <input
                    value={value.typed?.[1] || ""}
                    disabled={mode !== "edit"}
                    onFocus={() => setActiveTextField("typed:1")}
                    onBlur={() => setActiveTextField(null)}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        typed: {
                          ...(value.typed || {}),
                          1: e.target.value.toUpperCase()
                        }
                      })
                    }
                    style={{
                      width: "42px",
                      height: "42px",
                      textAlign: "center",
                      fontSize: "18px",
                      border:
                        mode === "review"
                          ? (feedback?.parts?.typed?.correct ||
                              feedback?.parts?.[1]?.correct)
                            ? "2px solid #2e7d32"
                            : "2px solid #c62828"
                          : "1px solid #aaa",
                      borderRadius: "4px"
                    }}
                  />

                  {
                    activeTextField === "typed:1" &&
                    mode === "edit" && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          marginTop: 4,
                          zIndex: 10
                        }}
                      >
                        <AccidentalButtons
                          onSelect={(acc) =>
                            onChange({
                              ...value,
                              typed: {
                                ...(value.typed || {}),
                                1: insertAccidental(
                                  value.typed?.[1] || "",
                                  acc
                                )
                              }
                            })
                          }
                        />
                      </div>
                    )}
                </div>

                {/* SUFFIX — EXACT STAFFINPUT PATTERN */}
                {config.suffix && (
                  <span
                    style={{
                      fontSize: 16,
                      color: "#000",
                      fontWeight: "bold",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {config.suffix}
                  </span>
                )}
              </div>
            </div>

            {/* ROMAN NUMERAL — COPIED FROM STAFFINPUT FUNCTIONAL DEGREE SELECTOR */}
            {Array.isArray(config.selectChoices) && config.answers?.choice && (
              <div
                style={{
                  marginTop: 0,
                  marginBottom: 10,
                  marginLeft: LEFT_MARGIN,
                  position: "relative"
                }}
              >
                {config.selectChoices.map((choice) => {
                  const isSelected = value.choice === choice;

                  const border =
                    mode === "review" && isSelected
                      ? feedback?.parts?.choice?.correct
                        ? "2px solid #2e7d32"
                        : "2px solid #c62828"
                      : isSelected
                      ? "2px solid #1976d2"
                      : "1px solid #aaa";

                  return (
                    <button
                      key={choice}
                      disabled={mode !== "edit"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        onChange({
                          ...value,
                          typed: value.typed || {},
                          choice
                        });
                      }}
                      style={{
                        marginRight: 8,
                        padding: "6px 14px",
                        fontSize: 16,
                        cursor: mode === "edit" ? "pointer" : "default",
                        background: isSelected ? "#e3f2fd" : "#fff",
                        border,
                        borderRadius: 4,
                        fontFamily: "Times New Roman, serif"
                      }}
                    >
                      {choice}
                    </button>
                  );
                })}
              </div>
            )}

            {/* BASS ROLE — SAME AS FUNCTIONAL DEGREE SELECTOR */}
            {Array.isArray(config.bassRoleChoices) && (
              <div
                style={{
                  marginTop: 0,
                  marginBottom: 10,
                  marginLeft: LEFT_MARGIN,
                  position: "relative"
                }}
              >
                {config.bassRoleChoices.map((choice) => {
                  const isSelected = value.bassRole === choice;

                  const border =
                    mode === "review" && isSelected
                      ? feedback?.parts?.bassRole?.correct
                        ? "2px solid #2e7d32"
                        : "2px solid #c62828"
                      : isSelected
                      ? "2px solid #1976d2"
                      : "1px solid #aaa";

                  return (
                    <button
                      key={choice}
                      disabled={mode !== "edit"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        onChange({
                          ...value,
                          typed: value.typed || {},
                          bassRole: choice
                        });
                      }}
                      style={{
                        marginRight: 8,
                        padding: "6px 14px",
                        fontSize: 16,
                        cursor: mode === "edit" ? "pointer" : "default",
                        background: isSelected ? "#e3f2fd" : "#fff",
                        border,
                        borderRadius: 4
                      }}
                    >
                      {choice}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {config.requestedRomanNumerals &&
  Object.entries(config.requestedRomanNumerals).map(([slot, numeral]) => {
    const i = Number(slot) - 1;
    const x =
      LEFT_MARGIN +
      firstSlotExtraOffset +
      i * SLOT_WIDTH +
      SLOT_WIDTH / 2 +
      8;

            return (
              <div
                key={`rn-${slot}`}
                style={{
                  position: "absolute",
                  left: x,
                  top:
                    TREBLE_STAFF_TOP +
                    LEDGER_ALLOWANCE +
                    STAFF_HEIGHT +
                    STAFF_GAP +
                    STAFF_HEIGHT +
                    LEDGER_ALLOWANCE +
                    50,
                  transform: "translateX(-50%)",
                  fontWeight: 600,
                  fontFamily: "myFirstFont",
                  pointerEvents: "none"
                }}
              >
                {numeral}
              </div>
            );
          })}

          {config.requestedAnalysisLabels &&
          Object.entries(config.requestedAnalysisLabels).map(([slot, label]) => {
            const i = Number(slot) - 1;
            const x =
  LEFT_MARGIN +
  firstSlotExtraOffset +
  i * SLOT_WIDTH +
  SLOT_WIDTH / 2 +
  8 +
  (config.labelLeft ? config.labelLeft : 0);
            const top = config.labelTop ? (TREBLE_STAFF_TOP + config.labelTop): TREBLE_STAFF_TOP +
                    LEDGER_ALLOWANCE +
                    STAFF_HEIGHT +
                    STAFF_GAP +
                    STAFF_HEIGHT +
                    LEDGER_ALLOWANCE +
                    74;
            return (
              <div
                key={`analysis-label-${slot}`}
                style={{
                  position: "absolute",
                  left: x,
                  top: top,
                  transform: "translateX(-50%)",
                  fontWeight: 600,
                  fontFamily: "times",
                  pointerEvents: "none"
                }}
              >
                {label}
              </div>
            );
          })}

          {PostStaffQuestions()}

      </div>
    </QuestionLayout>
  );
}

// function layoutChordNotes({
//   slot,
//   clef,
//   value,
//   givenPitches = [],
//   entries = []
// }) {
//   const AX_SHIFT = 12;

//   const notes = [];

//   givenPitches.forEach((pitch, index) => {
//     notes.push({
//       id: `given-${index}`,
//       pitch,
//       accidental: accidentalFromPitch(pitch),
//       noteOffsetX: 0,
//       accidentalOffsetX: undefined
//     });
//   });

//   entries.forEach((entry, index) => {
//     notes.push({
//       id: `entry-${index}`,
//       pitch: entry.pitch,
//       accidental: entry.accidental ?? accidentalFromPitch(entry.pitch),
//       noteOffsetX: 0,
//       accidentalOffsetX: undefined
//     });
//   });

//   const chord = notes
//     .map(note => ({
//       note,
//       i: pitchToStaffIndex(note.pitch, clef)
//     }))
//     .sort((a, b) => a.i - b.i);

//   const accidentalItems = chord.filter(item => item.note.accidental);

//   function conflicts(a, b) {
//     return Math.abs(a.i - b.i) <= 3;
//   }

//   const columns = [];

//   accidentalItems.forEach(item => {
//     let col = 0;

//     while (columns[col]?.some(other => conflicts(item, other))) {
//       col++;
//     }

//     if (!columns[col]) columns[col] = [];
//     columns[col].push(item);

//     item.note.accidentalOffsetX = -AX_SHIFT * (col + 1);
//   });

//   return Object.fromEntries(
//     notes.map(note => [note.id, note])
//   );
// }
function layoutChordNotes({
  slot,
  clef,
  value,
  givenPitches = [],
  entries = [],
  keySignature,
  skipAccidentalLayout = false
}) {
  const AX_SHIFT = 12;
  const DUPLICATE_NOTE_SHIFT = 17;

  const notes = [];

  givenPitches.forEach((pitch, index) => {
    const accidental = accidentalFromPitch(pitch);

    notes.push({
      id: `given-${index}`,
      pitch,
      accidental: shouldHideKeySignatureAccidental(
        pitch,
        accidental,
        keySignature
      )
        ? null
        : accidental,
      noteOffsetX: 0,
      accidentalOffsetX: undefined
    });
  });

  entries.forEach((entry, index) => {
    const accidental = entry.accidental ?? accidentalFromPitch(entry.pitch);

    notes.push({
      id: `entry-${index}`,
      pitch: entry.pitch,
      accidental: shouldHideKeySignatureAccidental(
        entry.pitch,
        accidental,
        keySignature
      )
        ? null
        : accidental,
      noteOffsetX: 0,
      accidentalOffsetX: undefined
    });
  });

  const pitchCounts = {};

  notes.forEach(note => {
    pitchCounts[note.pitch] = (pitchCounts[note.pitch] || 0) + 1;

    if (pitchCounts[note.pitch] > 1) {
      note.noteOffsetX =
        (pitchCounts[note.pitch] - 1) * DUPLICATE_NOTE_SHIFT;
    }
  });

  if (skipAccidentalLayout) {
    return Object.fromEntries(
      notes.map(note => [note.id, note])
    );
  }

  const chord = notes
    .map(note => ({
      note,
      i: pitchToStaffIndex(note.pitch, clef)
    }))
    .sort((a, b) => a.i - b.i);

  const accidentalItems = chord.filter(item => item.note.accidental);

  function conflicts(a, b) {
    return Math.abs(a.i - b.i) <= 3;
  }

  const columns = [];

  accidentalItems.forEach(item => {
    let col = 0;

    while (columns[col]?.some(other => conflicts(item, other))) {
      col++;
    }

    if (!columns[col]) columns[col] = [];
    columns[col].push(item);

    item.note.accidentalOffsetX = -AX_SHIFT * (col + 1);
  });

  return Object.fromEntries(
    notes.map(note => [note.id, note])
  );
}


function normalizeAccidentalGlyph(accidental) {
  if (!accidental) return null;

  return String(accidental)
    .replace(/♯/g, "#")
    .replace(/♭/g, "b")
    .replace(/𝄪|x/g, "##")
    .replace(/𝄫/g, "bb")
    .trim();
}

function keySignatureAccidentalForPitch(keySignature, pitch) {
  const letter = String(pitch || "").match(/^([A-G])/i)?.[1]?.toUpperCase();
  const entries = keySignature?.[1];

  if (!letter || !Array.isArray(entries)) return null;

  const match = entries.find(
    (entry) => String(entry?.pitch || "").charAt(0).toUpperCase() === letter
  );

  return normalizeAccidentalGlyph(match?.accidental);
}

function shouldHideKeySignatureAccidental(pitch, accidental, keySignature) {
  const noteAccidental = normalizeAccidentalGlyph(accidental);
  if (!noteAccidental || noteAccidental === "♮") return false;

  return (
    noteAccidental === keySignatureAccidentalForPitch(keySignature, pitch)
  );
}

function accidentalFromPitch(pitch) {
    if (!pitch) return null;
    if (pitch.includes("##")) return "##";
    if (pitch.includes("bb")) return "bb";
    if (pitch.includes("#")) return "#";
    if (pitch.includes("b")) return "b";
    if (pitch.includes("n") || pitch.includes("♮")) return "♮";
    return null;
}

function pitchForPosition(pitch) {
  return String(pitch || "").replace("n", "").replace("♮", "");
}

function keySignatureCount(accidentals) {
  return accidentals?.[1]?.length || 0;
}

function hasAnyKeySignature(config = {}, value = {}) {
  return (
    keySignatureCount(config.keySignature?.treble) > 0 ||
    keySignatureCount(config.keySignature?.bass) > 0 ||
    keySignatureCount(value.keySignature?.treble) > 0 ||
    keySignatureCount(value.keySignature?.bass) > 0
  );
}

function getMaxKeySignatureCount(config = {}, value = {}) {
  return Math.max(
    keySignatureCount(config.keySignature?.treble),
    keySignatureCount(config.keySignature?.bass),
    keySignatureCount(value.keySignature?.treble),
    keySignatureCount(value.keySignature?.bass)
  );
}

function getFirstSlotExtraOffset(count) {
  if (!count) return 0;

  // 1–3 accidentals usually fit fine.
  // 4+ need only a small push.
  return Math.max(0, (count - 3) * 10);
}